import { MathUtils } from './MathUtils'

export class AudioUtils {
  public static LOWEST_VOLUME_DBFS = -100
  public static HIGHTEST_VOLUME_DBFS = 0

  static async createAudioWorkletNode(context: BaseAudioContext, name: string, url: string) {
    try {
      return new AudioWorkletNode(context, name)
    } catch (err) {
      await context.audioWorklet.addModule(url)
      return new AudioWorkletNode(context, name)
    }
  }

  /** @return computes decibels relative to full scale */
  public static computeDecibels(amplitude: number): number {
    return 20 * Math.log10(amplitude)
  }

  /** @return volume level in decibels relative to full scale */
  public static getVolumeLevel(
    audioBuffer: AudioBuffer,
    options?: {
      trailingSamplesToConsider?: number
      removeProlonguedSilence?: {
        silenceDecibelLevel: number
        numberOfAllowedSilentSamples: number
      }
    },
  ): { averageVolume: number; channelVolumes: number[] } {
    // an audio buffer could contain multiple channels
    const channelVolumes = []

    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      let channel = audioBuffer.getChannelData(i)

      if (options?.trailingSamplesToConsider && channel.length < options.trailingSamplesToConsider) {
        // there are fewer samples than required
        continue
      }

      if (options?.removeProlonguedSilence) {
        const { silenceDecibelLevel, numberOfAllowedSilentSamples } = options.removeProlonguedSilence

        // remove prolonged silence
        let silentSamples = 0
        channel = channel.filter((sample) => {
          // convert to decibel
          const sampleDBFS = AudioUtils.computeDecibels(Math.abs(sample) /* [0, 1] */)
          if (sampleDBFS < silenceDecibelLevel) {
            silentSamples++
          } else {
            silentSamples = 0
          }

          return !(silentSamples > numberOfAllowedSilentSamples)
        })
      }

      // calculate percieved volume, clamped so dead channels report -100 instead of -Infinity
      const amplitudeRMS = MathUtils.getRootMeanSquared(channel, options?.trailingSamplesToConsider ? channel.length - options.trailingSamplesToConsider : 0, channel.length)
      const channelDecibels = Math.max(AudioUtils.computeDecibels(amplitudeRMS), AudioUtils.LOWEST_VOLUME_DBFS)
      channelVolumes.push(channelDecibels)
    }

    const liveChannelVolumes = channelVolumes.filter((v) => v > AudioUtils.LOWEST_VOLUME_DBFS)
    const averageVolume = MathUtils.average(liveChannelVolumes) ?? AudioUtils.LOWEST_VOLUME_DBFS

    return {
      averageVolume,
      channelVolumes,
    }
  }

  public static wasOnlySilenceRecorded(audioBuffer: AudioBuffer) {
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      const channel = audioBuffer.getChannelData(i)
      for (let j = 0; j < channel.length; j++) {
        const sample = channel[j]
        const sampleDBFS = AudioUtils.computeDecibels(Math.abs(sample))
        if (sampleDBFS !== Number.NEGATIVE_INFINITY) {
          return false
        }
      }
    }
    return true
  }

  /** @return true if the volume has exceeded a certain decibel level at any audio channel */
  public static hasClipped(audioBuffer: AudioBuffer, volumeClippingThreshold: number, samplesPerChunk: number): boolean {
    for (let i = 0; i < audioBuffer.numberOfChannels; i++) {
      const channel = audioBuffer.getChannelData(i)

      for (let i = 0; i < channel.length; i += samplesPerChunk) {
        // calculate percieved volume for sample
        const amplitudeRMS = MathUtils.getRootMeanSquared(channel, i, Math.min(channel.length, i + samplesPerChunk))

        const volume = AudioUtils.computeDecibels(amplitudeRMS)

        if (volume > volumeClippingThreshold) {
          return true
        }
      }
    }

    return false
  }

  public static createAudioContext = (): AudioContext | undefined => {
    const AudioContext =
      window.AudioContext ||
      (window as any).webkitAudioContext || // old Chrome / Safari
      (window as any).mozAudioContext // old Firefox

    if (AudioContext) return new AudioContext()

    return undefined
  }

  public static play = (url: string) => {
    const audio = new Audio(url)
    audio.loop = false
    audio.currentTime = 0
    audio.play()
  }

  public static isValidAudioSrcUrl(url: string): boolean {
    try {
      new URL(url) // throws an error if the URL is invalid
    } catch (_) {
      return false // the URL is not valid
    }

    // Convert url to lowercase to ensure the check isn't case-sensitive
    const lowerCaseUrl = url.toLowerCase()

    // Get the file extension
    const extension = lowerCaseUrl.split('.').pop()

    // Check if the URL ends with mp3 or wav
    if (extension === 'mp3' || extension === 'wav') {
      return true
    } else {
      return false
    }
  }

  public static playAudioFromUrl(url: string) {
    const audio = new Audio(url)

    audio.addEventListener('ended', function () {
      // Remove the audio element from the DOM after playing
      audio.remove()
    })

    audio.addEventListener('error', function () {
      // Handle audio playback error
      console.error('Error occurred during audio playback')
    })

    audio.play()
  }

  public static getAudioFileWithDurationFromBlob(file: File): Promise<{
    file: File
    duration: number
  }> {
    return new Promise(function (resolve) {
      const src = URL.createObjectURL(file)
      const audio = new Audio()
      audio.addEventListener('loadedmetadata', function () {
        resolve({ file, duration: audio.duration })
      })
      audio.src = src
    })
  }
}

export function getAudioFileWithDurationFromBlob(file: File): Promise<{
  file: File
  duration: number
}> {
  return new Promise(function (resolve) {
    const src = URL.createObjectURL(file)
    const audio = new Audio()

    // https://stackoverflow.com/questions/21522036/html-audio-tag-duration-always-infinity
    function getDuration(event: Event) {
      const el = event.target as HTMLAudioElement
      el.currentTime = 0
      el.removeEventListener('timeupdate', getDuration)
      resolve({
        file,
        duration: el.duration,
      })
    }

    audio.addEventListener('loadedmetadata', function () {
      if (audio.duration === Infinity || isNaN(audio.duration)) {
        audio.currentTime = Number.MAX_SAFE_INTEGER
        audio.addEventListener('timeupdate', getDuration)
      } else {
        resolve({ file, duration: audio.duration })
      }
    })
    audio.src = src
  })
}

/**
 * Ramps up the gain of a given GainNode over time to prevent a popping noise with abrupt volume changes.
 * https://alemangui.github.io/ramp-to-value
 *
 * @param gainNode - The GainNode to ramp up the gain for.
 * @param audioContext - The AudioContext used for the audio playback.
 */
export const rampUpGain = (gainNode: GainNode, audioContext: AudioContext) => {
  gainNode.gain.setTargetAtTime(1, audioContext.currentTime + 0.015, 0.015)
}

/**
 * Ramp down the gain of a given GainNode over time.
 * @param gainNode - The GainNode to ramp down.
 * @param audioContext - The AudioContext used for timing.
 */
export const rampDownGain = (gainNode: GainNode, audioContext: AudioContext) => {
  gainNode.gain.setTargetAtTime(0, audioContext.currentTime + 0.015, 0.015)
}

export const ACCEPTED_FILE_TYPES_FOR_STS: readonly string[] = [
  'audio/wav',
  'audio/wave',
  'audio/x-wave',
  'audio/vnd.wave',
  'audio/mpeg',
  'audio/mp3',
  'audio/ogg',
  'audio/flac',
  'audio/x-flac',
] as const

export const validateWAVHeader = (buffer: Uint8Array) => {
  // Check for 'RIFF' in the first 4 bytes and 'WAVE' in bytes 8 to 12
  return buffer[0] === 0x52 && buffer[1] === 0x49 && buffer[2] === 0x46 && buffer[3] === 0x46 && buffer[8] === 0x57 && buffer[9] === 0x41 && buffer[10] === 0x56 && buffer[11] === 0x45
}

export const validateMP3Header = (buffer: Uint8Array) => {
  // Check for 'ID3' or MPEG header; this is a simplified check and might need more robust handling
  return (
    (buffer[0] === 0x49 && buffer[1] === 0x44 && buffer[2] === 0x33) || // ID3v2 header
    ((buffer[0] & 0xff) === 0xff && (buffer[1] & 0xe0) === 0xe0)
  ) // MPEG 1 Layer III or MPEG 2 Layer III
}

/**
 * Returns an AudioBuffer padded to the given minimum duration with trailing silence.
 * If the input buffer already meets or exceeds the target duration, returns it unchanged.
 */
export const padAudioBufferWithSilence = (buffer: AudioBuffer, minDurationSeconds: number, audioContext: AudioContext): AudioBuffer => {
  if (buffer.duration >= minDurationSeconds) return buffer

  const targetLength = Math.ceil(minDurationSeconds * buffer.sampleRate)
  const padded = audioContext.createBuffer(buffer.numberOfChannels, targetLength, buffer.sampleRate)

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const source = buffer.getChannelData(channel)
    const destination = padded.getChannelData(channel)
    // Copy original samples; the rest of the destination stays as zeros (silence).
    destination.set(source, 0)
  }

  return padded
}

/**
 * Encodes an AudioBuffer to a 16-bit PCM WAV Blob (no external deps).
 */
export const encodeAudioBufferToWav = (buffer: AudioBuffer): Blob => {
  const numChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const bitsPerSample = 16
  const bytesPerSample = bitsPerSample / 8
  const blockAlign = numChannels * bytesPerSample
  const byteRate = sampleRate * blockAlign
  const numSamples = buffer.length
  const dataSize = numSamples * blockAlign
  const headerSize = 44
  const totalSize = headerSize + dataSize

  const arrayBuffer = new ArrayBuffer(totalSize)
  const view = new DataView(arrayBuffer)

  // Interleave channel data
  const channels: Float32Array[] = []
  for (let c = 0; c < numChannels; c++) {
    channels.push(buffer.getChannelData(c))
  }

  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i))
  }

  // RIFF header
  writeString(0, 'RIFF')
  view.setUint32(4, 36 + dataSize, true)
  writeString(8, 'WAVE')

  // fmt sub-chunk
  writeString(12, 'fmt ')
  view.setUint32(16, 16, true) // PCM chunk size
  view.setUint16(20, 1, true) // format = PCM
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, byteRate, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitsPerSample, true)

  // data sub-chunk
  writeString(36, 'data')
  view.setUint32(40, dataSize, true)

  // Write interleaved PCM samples
  let offset = 44
  for (let i = 0; i < numSamples; i++) {
    for (let c = 0; c < numChannels; c++) {
      // Clamp and convert Float32 (-1..1) to Int16
      let sample = channels[c][i]
      sample = Math.max(-1, Math.min(1, sample))
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true)
      offset += 2
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' })
}
